import PropTypes from "prop-types";
import React, { useEffect, useState, useRef } from "react";
import { reduxForm, formValueSelector } from "redux-form";
import { useSelector, useDispatch } from "react-redux";
import { connect } from "react-redux";
import moment from "moment";
import { saveClaimDraft, getCurrencyExchangeRates, clearError } from "../../actions/claimActionsPP";
import { Button } from "cxa-components";
import { intlShape, injectIntl, FormattedMessage } from "react-intl";
import messages from "components/messagesDescription";
import _ from "lodash";
import { CloseIcon } from "../common/icons";
import { intlFormat } from "i18n/util";
import { ErrorMessage } from "../common/ErrorMessage";
import { filterNull } from "cxa-utils";
import { ConfirmationDialog } from "./widgets/ConfirmationDialog";
import { othersFields, othersOverseasFields } from "../constants";
import {
  StringField,
  LongStringField,
  DateField,
  DecimalField,
  ComboBoxField,
  AutocompleteField,
  CheckboxField,
  IntegerField,
} from "cxa-form-field";
import ReceiptPreviewField from "./ReceiptPreviewField";
import { isDecimal, isDecimalZeroAllowed, isPostalCode } from "./ClaimSubmissionForm";
import ClaimSubmissionGuideline from "./widgets/ClaimSubmissionGuideline";
import { changeCurrencyRate } from "../../actions/claimActions";
import { Prompt } from "react-router-dom";
import withMatchMediaProps from "@hocs/with-match-media-props";
import { decodeHtmlContent } from "../../utils";

const getFieldComponent = (field, claim) => {
  const { name, items, dataType, lookupList } = field;

  let fieldType = dataType;
  if (name === "provider" && items && items.length && claim) {
    fieldType = items.find(item => claim.hasOwnProperty(name) && item.value === claim[name])
      ? dataType
      : "Autocomplete";
  }
  if (lookupList === "TransportType" && items && items.length) {
    fieldType = "Autocomplete";
  }

  switch (fieldType) {
    case "List":
      return ComboBoxField;
    case "Date":
      return DateField;
    case "Decimal":
      return DecimalField;
    case "Autocomplete":
      return AutocompleteField;
    case "TextArea":
      return LongStringField;
    case "Boolean":
      return CheckboxField;
    case "Integer":
      return IntegerField;
    default:
      return StringField;
  }
};

const validateRequired = (value, values, { intl: { formatMessage } }) =>
  value || value === 0 ? undefined : formatMessage(messages.requiredDescription);

const validatePostalCode = (value, values, { intl: { formatMessage } }) =>
  isPostalCode(value) ? undefined : formatMessage(messages.invalidPostalCodeDescription);

const validateInteger = (value, values, { intl: { formatMessage } }) =>
  isDecimal(value) ? undefined : formatMessage(messages.invalidNumberDescription);

const validateDecimal = (value, values, { intl: { formatMessage } }) =>
  isDecimal(value) ? undefined : formatMessage(messages.invalidAmountDescription);

const validateDecimalZeroAllowed = (value, values, { intl: { formatMessage } }) =>
  isDecimalZeroAllowed(value) ? undefined : formatMessage(messages.invalidAmountDescription);

const convertFieldToInput = (
  field,
  intl,
  toCurrencyCode,
  receiptCurrency,
  foreignReceiptAmount,
  handleProviderFieldBlur,
  handleProviderFieldChange
) => {
  const { required, dataType, disabled, validDateRange } = field;
  field.label = intlFormat(intl, {
    id: field.label,
    defaultMessage: field.label,
  });

  if (field.dataType === "List" && field.items) {
    field.items.map(item => {
      item.label = intlFormat(intl, {
        id: item.id,
        defaultMessage: item.label,
      });
    });
  }

  if (field.name === "receiptAmount" && toCurrencyCode) {
    field.label = `${field.label} (${toCurrencyCode})`;
  }

  if (field.name === "receiptCurrency") {
    field.selectDefaultValue = receiptCurrency;
    field.foreignReceiptAmount = foreignReceiptAmount;
  }

  if (field.name == "receiptDate" && validDateRange) {
    field.minDate = moment(validDateRange.start);
    field.maxDate = moment(validDateRange.end);
  }

  const comp = getFieldComponent(field);

  return React.createElement(comp, {
    id: field.name,
    key: field.name,
    description: intl.formatMessage({
      id: field.toolTip,
      defaultMessage: field.toolTip,
    }),
    helpText: intl.formatMessage({
      id: `claims.helpText.${field.name}`,
      defaultMessage: "#void help text#",
    }),
    validate: [
      required && validateRequired,
      dataType === "Integer" && validateInteger,
      dataType === "Decimal" &&
        (field.name === "consultationFee" || /^dec\d$/.test(field.name)) &&
        validateDecimalZeroAllowed,
      dataType === "Decimal" &&
        field.name !== "consultationFee" &&
        !/^dec\d$/.test(field.name) &&
        validateDecimal,
      dataType === "String" && field.name === "postalCode" && validatePostalCode,
    ].filter(filterNull),
    ...field,
    dateFormat: moment.localeData().longDateFormat("ll"),
    readOnly: disabled,
    disabled,
    onBlur: dataType === "Autocomplete" && field.name === "provider" && handleProviderFieldBlur,
    onChange: dataType === "Autocomplete" && field.name === "provider" && handleProviderFieldChange,
  });
};

const ClaimSubmissionFormPP = props => {
  const currentURL = window.location.href;
  const isEditURL = /\/claims\/edit/;

  const dispatch = useDispatch();
  const claimItemData = useSelector(state => state.claimsPP.claimItem);
  const ocrValues = useSelector(state => state.claimsPP.ocrValues);
  const error = useSelector(state => state.claimsPP.error);
  const currencyExchangeRates = useSelector(state => state.claimsPP.currencyExchangeRates);
  const skipReceipt = useSelector(state => state.claimsPP.skipReceipt);

  const isEdit = isEditURL.test(currentURL);
  const [showUploadReceiptGuideline, setShowUploadReceiptGuideline] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);
  const {
    change,
    handleSubmit,
    submitting,
    intl,
    toCurrencyCode,
    foreignReceiptAmount,
    receiptCurrency,
    receiptDate,
    benefitPeriod,
    push,
    hospitalField,
  } = props;

  const handleLinkClick = event => {
    event.preventDefault();
    setShowUploadReceiptGuideline(true);
  };

  const handleProviderFieldBlur = () => {
    setIsBlurred(true);
  };

  const handleProviderFieldChange = () => {
    setIsBlurred(false);
  };

  const receiptGuidelineRef = useRef(null);

  const isLastDecimalZero = num => {
    let numStr = num.toString();
    return numStr.includes(".") && numStr[numStr.length - 1] == "0";
  };

  useEffect(() => {
    if (receiptGuidelineRef.current) {
      receiptGuidelineRef.current.addEventListener("click", handleLinkClick);
    }

    return () => {
      if (receiptGuidelineRef.current) {
        receiptGuidelineRef.current.removeEventListener("click", handleLinkClick);
      }
    };
  }, []);

  useEffect(() => {
    !isNaN(foreignReceiptAmount) &&
      foreignReceiptAmount > 0 &&
      !isLastDecimalZero(foreignReceiptAmount) &&
      convertCurrencyRate(
        parseFloat(foreignReceiptAmount),
        "foreignReceiptAmount",
        receiptCurrency
      );
  }, [foreignReceiptAmount, receiptCurrency, currencyExchangeRates]);

  useEffect(() => {
    benefitPeriod &&
      receiptDate &&
      receiptCurrency &&
      dispatch(getCurrencyExchangeRates(benefitPeriod, receiptDate, receiptCurrency));
  }, [benefitPeriod, receiptDate, receiptCurrency]);

  if (!claimItemData) return "";

  const convertCurrencyRate = (amount, targetName, currencyCode) => {
    const currencyField = fields.find(
      field => field.name === "receiptCurrency" && field.childFieldId
    );

    const currencyConfig =
      Array.isArray(currencyExchangeRates) && currencyExchangeRates.length > 0
        ? currencyExchangeRates
        : currencyField && currencyField.config;

    const exchangeRates =
      currencyConfig &&
      currencyConfig.filter(code =>
        currencyCode ? code.currencyFrom === currencyCode : code.currencyFrom === receiptCurrency
      );

    // Check if there's currencyConfig available before converting
    //      to avoid breaking the receipt amount without foreign currency conversion
    if (exchangeRates && exchangeRates.length > 0) {
      const { currencyFrom = null, currencyTo = null, exchangeRate } = exchangeRates[0];

      // convert currency rate
      // removed fixed decimal place in foreignReceiptAmount so that conversion can run smoothly while amount is being entered one number at a time.
      if (currencyTo !== currencyFrom) {
        change("receiptAmount", (amount * exchangeRate).toFixed(2));
        change("foreignReceiptAmount", amount);
        dispatch(changeCurrencyRate((amount * exchangeRate).toFixed(2), currencyTo));
      } else if (currencyTo && currencyFrom) {
        change("receiptAmount", amount.toFixed(2));
        change("foreignReceiptAmount", amount);
        dispatch(changeCurrencyRate(amount.toFixed(2), currencyTo));
      } else {
        // invalidate receipt amount
        change("receiptAmount", null);
      }
    } else if (targetName === "foreignReceiptAmount") {
      change("foreignReceiptAmount", amount);
    } else {
      change("receiptAmount", amount);
    }
  };

  const confirmDuplicateSubmission = () => {
    const { onSubmit, handleSubmit } = props;
    setTimeout(
      handleSubmit((values, dispatch) =>
        onSubmit({ ...values, userAccepted: true, limitAccepted: false }, dispatch, props)
      )
    );
  };

  const cancelSubmission = () => {
    dispatch(clearError());
  };

  const confirmLimitExceedSubmission = () => {
    const { change, onSubmit, handleSubmit } = props;
    setTimeout(
      handleSubmit((values, dispatch) =>
        onSubmit({ ...values, limitAccepted: true }, dispatch, props)
      )
    );
  };

  const goToPrevious = () => {
    push("/claims/submit");
  };
  const formFields = claimItemData.formFields || [];
  const receiptField = formFields.find(x => x.name == "receipt");

  let isValidProvider = true;
  const hasProviderValue = hospitalField && hospitalField.length >= 1;
  for (const field of formFields) {
    if (field.name === "provider") {
      isValidProvider = false;
      if (field.dataType === "String") {
        isValidProvider = hasProviderValue;
      } else if (field.dataType === "Autocomplete") {
        if (!isValidProvider && hospitalField) {
          const hospitalFieldDecode = decodeHtmlContent(hospitalField);
          isValidProvider = field.items.some(clinic => clinic.value === hospitalFieldDecode);
        }
      }
      if (field.readOnly || field.disabled) {
        isValidProvider = true;
      }
    }
  }

  if (isBlurred && !isValidProvider && hasProviderValue) {
    change("provider", "");
  }
  const getProviderFields = hospitalField => {
    if (!hospitalField) return [];

    const lowerHospitalField = hospitalField.toLowerCase();

    let fieldsToFormat = [];
    if (lowerHospitalField === "others") {
      fieldsToFormat = othersFields;
    } else if (lowerHospitalField === "others-overseas") {
      fieldsToFormat = othersOverseasFields;
    }

    return fieldsToFormat.map(field => ({
      ...field,
      label: intl.formatMessage({ id: field.label, defaultMessage: field.label }),
    }));
  };

  const providerFields = getProviderFields(hospitalField);

  const fields = formFields
    .filter(field => field.name !== "receipt")
    .map(field => {
      if (field.name === "provider" && field.dataType === "Autocomplete") {
        return [field, ...providerFields];
      } else {
        return field;
      }
    })
    .flat();

  const hasForeignReceipt = fields.find(x => x.name == "foreignReceiptAmount");

  //auto populate the field color base on the ocr values.

  const claimantField = fields.find(x => x.name === "claimantName");
  if (claimantField) {
    claimantField.disabled = true;
  }

  if (ocrValues && ocrValues.status === "success" && ocrValues.fields) {
    fields.forEach(field => {
      const ocrField =
        field.name === "foreignReceiptAmount"
          ? ocrValues.fields.find(x => x.name === "receiptAmount")
          : ocrValues.fields.find(x => x.name === field.name);

      if (!ocrField) {
        return;
      }

      switch (field.name) {
        case "foreignReceiptAmount":
        case "receiptAmount":
          if (field.name === "receiptAmount" && hasForeignReceipt) {
            break;
          }
          field.color = ocrField.color;
          break;

        case "claimantName":
          if (field.dataType === "List" && Array.isArray(field.items)) {
            const hasItem = field.items.some(item => item.label === ocrField.value);
            if (hasItem) {
              field.color = ocrField.color;
            }
          }
          break;

        case "provider":
          if (
            Array.isArray(field.items) &&
            !field.items.some(item => item.value === ocrField.value)
          ) {
            field.color = null;
          } else {
            field.color = ocrField.color;
          }
          break;

        default:
          field.color = ocrField.color;
          break;
      }
    });
  }

  const infoFields = [
    {
      label: intl.formatMessage(messages.benefitPlanDescription),
      value: claimItemData.planYear && claimItemData.planYear.planID,
      id: "planName",
    },
    {
      label: intl.formatMessage(messages.ClaimItemTypeDescription),
      value: intl.formatMessage({
        id: claimItemData.claimFormTitle,
        defaultMessage: claimItemData.claimFormTitle,
      }),
      id: "claimName",
    },
    ...(claimItemData.limitMessages || []),
  ];

  const promptIsOpen = error && (error.status === 430 || error.status === 409);
  const promptMessage = promptIsOpen && error.body;
  let errorMsg = !promptIsOpen && error && <ErrorMessage error={error.body} intl={intl} />;
  const hasKeyReceiptGuideline = intl.messages.hasOwnProperty("config.receiptUploadGuide");

  return (
    <form onSubmit={handleSubmit}>
      <div className="claim-form-container">
        <ConfirmationDialog
          instructions={promptMessage}
          isOpen={promptIsOpen}
          handleOk={
            error && error.status === 430
              ? confirmLimitExceedSubmission
              : confirmDuplicateSubmission
          }
          handleCancel={error && cancelSubmission}
        />
        <div className="claim-submission-form-container">
          <div className="claim-submissions-form-details">
            {infoFields.map(({ id, label, value }) => (
              <div key={id}>
                <FormattedMessage id={label} defaultMessage={label} />
                {": "}
                <span id={id}>
                  <strong>
                    <FormattedMessage id={value} defaultMessage={value} />
                  </strong>
                </span>
              </div>
            ))}
            {claimItemData.claimLimitMessages &&
              claimItemData.claimLimitMessages.map(({ message, value }) => (
                <div key={message}>
                  <FormattedMessage id={message} defaultMessage={message} />
                  <span id={message}>
                    <strong>
                      <FormattedMessage id={value} defaultMessage={value} />
                    </strong>
                  </span>
                </div>
              ))}

            {!skipReceipt && (
              <ClaimSubmissionGuideline
                title={intl.formatMessage(messages.claims.claimSubmissionGuideline)}
              >
                {hasKeyReceiptGuideline ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: intl.messages["config.receiptUploadGuide"],
                    }}
                  />
                ) : (
                  <div
                    ref={receiptGuidelineRef}
                    dangerouslySetInnerHTML={{ __html: claimItemData.claimSubmissionGuideline }}
                  />
                )}
              </ClaimSubmissionGuideline>
            )}
            {showUploadReceiptGuideline && (
              <div style={{ margin: "1rem 0" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <a>{intl.formatMessage(messages.claims.uploadReceiptGuideline)}</a>
                  <CloseIcon
                    style={{
                      cursor: "pointer",
                    }}
                    onClick={() => setShowUploadReceiptGuideline(false)}
                  />
                </div>
                <div
                  dangerouslySetInnerHTML={{ __html: claimItemData.claimInstructionReceiptUpload }}
                />
              </div>
            )}
          </div>
          {receiptField && (
            <ReceiptPreviewField
              {...props}
              {...receiptField}
              label={intlFormat(intl, {
                id: receiptField.label,
                defaultMessage: receiptField.label,
              })}
              ocrValues={ocrValues}
            />
          )}
        </div>

        <div className="cxa-grid__item--six">
          <fieldset>
            {fields.map(field => {
              return convertFieldToInput(
                field,
                intl,
                toCurrencyCode,
                receiptCurrency,
                foreignReceiptAmount,
                handleProviderFieldBlur,
                handleProviderFieldChange
              );
            })}
          </fieldset>
        </div>
      </div>
      {errorMsg}
      <div className="claim-submit-button-container claim-button-container-responsive">
        <Button type="button" disabled={submitting || isEdit} secondary onClick={goToPrevious}>
          {intl.formatMessage(messages.claims.goPrevious)}
        </Button>
        <Button primary type="submit" submitting={submitting} disabled={!isValidProvider}>
          {intl.formatMessage(messages.claims.saveDraftClaim)}
        </Button>
      </div>
      <Prompt
        when={!submitting}
        message={intl.formatMessage(messages.claims.saveClaimInformationDescription)}
      />
    </form>
  );
};

ClaimSubmissionFormPP.propTypes = {
  fields: PropTypes.object,
  handleSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  claimItem: PropTypes.object,
  error: PropTypes.string,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  intl: intlShape.isRequired,
  change: PropTypes.func,
  changeFieldValue: PropTypes.func,
  onSubmit: PropTypes.func,
  hospitalField: PropTypes.string,
  receiptDate: PropTypes.string,
  benefitPeriod: PropTypes.string,
};

const selector = formValueSelector("addClaimForm");

export default connect(state => {
  // can select values individually
  const foreignReceiptAmount = selector(state, "foreignReceiptAmount");
  const receiptCurrency = selector(state, "receiptCurrency");
  const receiptDate = selector(state, "receiptDate");
  const benefitPeriod = selector(state, "benefitPeriod") || state.claimsPP.selectedPlanYear;
  const hospitalField = selector(state, "provider");
  return {
    foreignReceiptAmount,
    receiptCurrency,
    receiptDate,
    benefitPeriod,
    hospitalField,
  };
})(
  injectIntl(
    reduxForm({
      form: "addClaimForm",
      onSubmit: saveClaimDraft,
      enableReinitialize: true,
      keepDirtyOnReinitialize: true,
      forceUnregisterOnUnmount: true,
    })(
      withMatchMediaProps({
        isNarrowScreen: {
          maxWidth: 680,
        },
      })(ClaimSubmissionFormPP)
    )
  )
);



// WEBPACK FOOTER //
// ./components/claim/ClaimSubmissionFormPP.js